import type { CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';

import { CheckCircle2Icon, TextIcon, TypeIcon, MapPinnedIcon } from 'lucide-react';
import { useState } from 'react';

import type { TabValue } from '@/hooks/meeting';

import MeetingCloseAlert from '@/components/features/calendar/form/meeting-form-content/MeetingCloseAlert';
import MeetingDateAndTimeFields from '@/components/features/calendar/form/meeting-form-content/MeetingDateAndTimeFields';
import MeetingFormContentLabelIcon from '@/components/features/calendar/form/meeting-form-content/MeetingFormContentLabelIcon';
import MeetingFormHeader from '@/components/features/calendar/form/meeting-form-content/MeetingFormHeader';
import { ParticipantsSection } from '@/components/features/calendar/form/meeting-form-content/ParticipantsSection';
import { TypeRadioGroupField } from '@/components/features/calendar/form/meeting-form-content/TypeRadioGroupField';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogFooter } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useMeetingForm, useMeetingFormTabs, useParticipantSelection } from '@/hooks/meeting';

import { GroupsSelectTab, UsersSelectTab } from './MeetingFormContentSelectTabs';

interface MeetingFormContentProps {
  callback: (data: CalendarUpsertSchema) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData?: Partial<CalendarUpsertSchema>;
  defaultValues?: Partial<CalendarUpsertSchema>;
}

export function MeetingFormContent(meetingFormContentProps: MeetingFormContentProps) {
  const { callback, open, onOpenChange, formData, defaultValues } = meetingFormContentProps;
  const [openCloseAlert, setOpenCloseAlert] = useState<boolean>(false);
  const { form, groups, users } = useMeetingForm({
    open,
    formData,
    defaultValues,
  });

  const { tabs, setTabs, searchValue, setSearchValue, searchDebouncedValue, isMainTab, isUsersSelectTab, isGroupsSelectTab, goToUsersTab, goToGroupsTab } = useMeetingFormTabs();

  const { handleSelectedUsersChange, handleSelectedGroupsChange, onRemoveUser, onRemoveGroup } = useParticipantSelection({
    users,
    groups,
    setValue: form.setValue,
  });

  const handleSubmit = (data: CalendarUpsertSchema) => {
    callback(data);
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const handleCloseConfirmation = () => {
    setOpenCloseAlert(false);
    handleClose();
  };

  return (
    <>
      <MeetingCloseAlert open={openCloseAlert} onOpenChange={setOpenCloseAlert} confirmAction={handleCloseConfirmation} />
      <DialogBody>
        <MeetingFormHeader tabs={tabs} setTabs={setTabs} searchValue={searchValue} setSearchValue={setSearchValue} />
        <Tabs
          value={tabs}
          onValueChange={(value) => {
            setTabs(value as TabValue);
          }}
          className={'flex grow flex-col gap-3 overflow-y-auto'}
        >
          <TabsContent value={'main'}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className={'flex flex-col gap-5 py-4'}>
                <div className={'flex gap-3 px-4'}>
                  <MeetingFormContentLabelIcon>
                    <TypeIcon />
                  </MeetingFormContentLabelIcon>
                  <FormField
                    name={'name'}
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className={'w-full'}>
                        <FormLabel>Nazwa</FormLabel>
                        <FormControl>
                          <Input placeholder={'Nazwa spotkania'} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className={'flex gap-3 px-4'}>
                  <MeetingFormContentLabelIcon>
                    <TextIcon />
                  </MeetingFormContentLabelIcon>
                  <FormField
                    name={'desc'}
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className={'w-full'}>
                        <FormLabel>
                          Opis <small className={'text-text-tertiary text-xs'}>(opcionalne)</small>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={'Opis spotkania'}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(event) => {
                              const value = event.target.value;
                              field.onChange(value ? value : null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                <MeetingDateAndTimeFields form={form} />

                <div className={'flex gap-3 px-4'}>
                  <MeetingFormContentLabelIcon>
                    <MapPinnedIcon />
                  </MeetingFormContentLabelIcon>
                  <TypeRadioGroupField form={form} />
                </div>

                <Separator />

                <ParticipantsSection users={users} groups={groups} onRemoveUser={onRemoveUser} onRemoveGroup={onRemoveGroup} onAddUsers={goToUsersTab} onAddGroups={goToGroupsTab} />
              </form>
            </Form>
          </TabsContent>

          <TabsContent value={'usersSelect'} className={'p-4'}>
            <UsersSelectTab selected={users} setSelected={handleSelectedUsersChange} searchValue={searchDebouncedValue} enabled={isUsersSelectTab} />
          </TabsContent>

          <TabsContent value={'groupsSelect'} className={'p-4'}>
            <GroupsSelectTab selected={groups} setSelected={handleSelectedGroupsChange} searchValue={searchDebouncedValue} enabled={isGroupsSelectTab} />
          </TabsContent>
        </Tabs>
      </DialogBody>

      {isMainTab ? (
        <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <Button onClick={handleClose} type={'button'} variant={'tertiary'} className={'w-full sm:w-fit'}>
            Anuluj
          </Button>
          <Button onClick={form.handleSubmit(handleSubmit)} type={'submit'} variant={'primary'} className={'w-full sm:w-fit'}>
            <CheckCircle2Icon />
            Zapisz
          </Button>
        </DialogFooter>
      ) : (
        <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'}>
            Zapisz
          </Button>
        </DialogFooter>
      )}
    </>
  );
}
